import { createTheme, CSSInterpolation, ThemeOptions } from '@mui/material/styles'
// in your theme file that you call `createTheme()`
import { ThemeMode } from './ThemeStore'
import type { } from '@mui/x-data-grid/themeAugmentation';
import { themeConfig, gapAubergine, creamOverlayBase } from '@gap/shared/theme'
import { prefersDarkMode } from 'reusable/utils/utils'

export interface IThemeVariables {
   themeMode: ThemeMode
   genericOpacity: number
   pdfFormat: boolean
}

export const gapPassionPalette = {
   main: themeConfig.passion,
   light: '#F28C64', // optional complementary shades
   dark: '#C44F28',
   contrastText: '#FFFFFF',
}

export const createGapTheme = (themeOptions: IThemeVariables) => {
   const mode: NonNullable<ThemeOptions['palette']>['mode'] = themeOptions.themeMode === 'os-defined' ? (prefersDarkMode() ? 'dark' : 'light') : themeOptions.themeMode
   const options: ThemeOptions = {
      palette: {
         mode,
         gap: {
            primary: themeConfig.primary,
            info: {
               "500": "#378CEF"
            }
         },
         passion: gapPassionPalette,
         primary: {
            main: gapAubergine,
         },
         secondary: {
            main: gapAubergine,
         },
         success: {
            main: gapAubergine,
         },
         info: {
            main: "#378CEF",
            "50": "#EBF4FD",
            "600": "#2C70BF",
            "800": "#163860"
         },
         warning: {
            main: '#F9DBAF',
            dark: '#B77E2A',
            contrastText: '#30210B'
         },
         action: {
            hover: themeConfig.light.action.hover,
            hoverOpacity: themeConfig.creamAlpha.hover,
            selected: themeConfig.light.action.selected,
            selectedOpacity: themeConfig.creamAlpha.selected,
            focus: themeConfig.light.action.focus,
            focusOpacity: themeConfig.creamAlpha.focus,
         },
         divider: themeConfig.light.divider,
      },
      typography: themeConfig.typographyProps,
      components: {
         MuiDialog: {
            styleOverrides: {
               root: {
                  zIndex: themeConfig.dialog.zIndex
               }
            }
         },
         MuiCssBaseline: {
            styleOverrides: {
               html: {
                  fontSize: themeOptions.pdfFormat ? '70%' : '100%'
               }
            },
         },
         MuiListItemText: {
            styleOverrides: {
               root: {
                  overflowX: 'hidden',
               },
               primary: {
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
               },
               secondary: {
                  // Wraps freely (no line clamp yet) — explicit 'normal' is needed since
                  // whiteSpace is inherited and MuiListItemButton's root forces 'nowrap'.
                  whiteSpace: 'normal',
               },
            }
         },
         MuiListItemButton: {
            styleOverrides: {
               root: ({ theme }) => ({
                  overflowX: 'hidden',
                  whiteSpace: 'nowrap',
                  '&.Mui-selected': {
                     backgroundColor: theme.palette.action.selected,
                     '.MuiTypography-root': {
                        fontWeight: 'bold!important'
                     },
                     '&:hover': {
                        backgroundColor: themeConfig.light.action.selectedHover,
                     }
                  }
               })
            }
         },
         MuiInputBase: {
            styleOverrides: {
               root: {
                  '& fieldset': {
                     borderRadius: '8px',
                  },
                  '& .MuiInputBase-root': {
                     '& fieldset': {
                        borderRadius: '8px',
                     },
                  },
               },
            },
         },
         MuiOutlinedInput: {
            styleOverrides: {
               root: ({ theme }) => ({
                  backgroundColor: theme.palette.common.white,
                  borderRadius: '8px',
                  '& fieldset': {
                     borderColor: themeConfig.light.borderColour,
                  },
               }),
            },
         },
         MuiFormControl: {
            styleOverrides: {
               root: {
                  '& .MuiTextField-root': {
                     '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                           borderRadius: '8px',
                        },
                     },
                  }
               }
            }
         },
         MuiTextField: {
            styleOverrides: {
               root: {
                  '& input[type=number]': {
                     MozAppearance: 'textfield'
                  },
                  '& .MuiOutlinedInput-root': {
                     '& fieldset': {
                        borderRadius: '8px',
                     },
                  },
               },
            },
         },
         MuiTab: {
            styleOverrides: {
               root: {
                  textTransform: 'none',
               },
            },
         },
         MuiTableRow: {
            styleOverrides: {
               root: {
                  '&.gap-table-row-clickable': {
                     cursor: `pointer`,
                  }
               },
            },
         },
         MuiTouchRipple: {
            styleOverrides: {
               ripple: {
                  '& .MuiTouchRipple-child': {
                     backgroundColor: creamOverlayBase,
                  },
               },
            },
         },
         MuiIconButton: {
            defaultProps: {
               color: 'primary',
            },
         },
         MuiButton: {
            styleOverrides: {
               root: {
                  borderRadius: '2em',
                  textTransform: 'none',
               },
               outlined: ({ theme }) => ({
                  borderColor: gapAubergine,
                  '&:hover': {
                     backgroundColor: theme.palette.action.hover,
                  },
               }),
               text: ({ theme }) => ({
                  '&:hover': {
                     backgroundColor: theme.palette.action.hover,
                  },
               }),
            },
            variants: [
               {
                  props: { size: 'extra-small' },
                  style: {
                     lineHeight: 1.2,
                     padding: '4px 5px',
                     fontSize: '13px'
                  },
               },
            ],
         },
         MuiButtonGroup: {
            styleOverrides: {
               root: {
                  borderRadius: '2em',
               },
            },
         },
         MuiStack: {
            styleOverrides: {
               root: {
                  opacity: (themeOptions.genericOpacity ? themeOptions.genericOpacity / 10 : 100) + '%'
               }
            }
         },
         MuiPaper: {
            styleOverrides: {
               root: {
                  borderRadius: '8px',
               },
            },
         },
         MuiCard: {
            styleOverrides: {
               root: {
                  boxShadow: 'none',
                  border: `1px solid ${themeConfig.light.border}`,
               },
            },
         },
         MuiAppBar: {
            styleOverrides: {
               root: muiAppBarStyleOverrides,
            },
         },
         MuiChip: {
            styleOverrides: {
               root: {
                  fontWeight: 500,
               },
            },
         },
         MuiSnackbar: {
            styleOverrides: {
               root: {
                  '& .MuiPaper-root': {
                     borderRadius: '2em',
                  },
               },
            },
         },
         MuiMenuItem: {
            styleOverrides: {
               root: {
                  borderRadius: '1.5em'
               }
            }
         },
         MuiFab: {
            styleOverrides: {
               root: {
                  position: 'fixed',
                  bottom: '3em',
                  right: '3em',
               },
            },
         },
         MuiDrawer: {
            styleOverrides: {
               root: {
                  '& .MuiPaper-root:not(.MuiAlert-root)': {
                     borderRadius: '0',
                  },
               },
            },
         },
         MuiStepper: {
            styleOverrides: {
               root: {
                  background: '#ffffff'
               }
            }
         },
         MuiAccordion: {
            styleOverrides: {
               root: {
                  // Structural panel border, not a control border — see MuiCard above.
                  border: `1px solid ${themeConfig.light.border}`,
                  '&::before': {
                     display: 'none',
                  },
                  '&:first-of-type': {
                     borderTopLeftRadius: '8px!important',
                     borderTopRightRadius: '8px!important',
                  },
                  '&:last-of-type': {
                     borderBottomLeftRadius: '8px!important',
                     borderBottomRightRadius: '8px!important',
                  },
               },
            },
         },
         MuiAccordionSummary: {
            styleOverrides: {
               root: {
                  '& .MuiAccordionSummary-expandIconWrapper': {
                     color: 'inherit'
                  },
               },
            },
         },
         MuiAccordionDetails: {
            styleOverrides: {
               root: {
                  paddingTop: 0,
               },
            },
         },
         MuiAutocomplete: {
            styleOverrides: {
               popper: {
                  zIndex: themeConfig.autoCompletePopper.zIndex,
                  '& .MuiListSubheader-root': {
                     top: '-8px',
                     backgroundColor: mode === 'dark' ? themeConfig.grey[800] : '#fff',
                  },
               },
            },
         },
         MuiTooltip: {
            styleOverrides: {
               tooltip: {
                  backgroundColor: `${themeConfig.primary['800']}`,
                  opacity: 0.85,
               },
               arrow: {
                  color: themeConfig.primary['800'],
                  opacity: 0.85,
               },
            },
         },
         MuiSvgIcon: {
            variants: [
               {
                  props: {
                     fontSize: 'extra-large'
                  },
                  style: {
                     fontSize: '64px'
                  },
               },
               {
                  props: {
                     fontSize: 'extra-small'
                  },
                  style: {
                     fontSize: '16px'
                  },
               }
            ],
         },
         MuiTypography: {
            styleOverrides: {
               root: ({ theme, ownerState }) => ({
                  ...(ownerState['max-lines'] && {
                     overflow: 'hidden',
                     textOverflow: 'ellipsis',
                     display: '-webkit-box',
                     WebkitLineClamp: ownerState['max-lines'],
                     WebkitBoxOrient: 'vertical',
                  }),
               }),
            },
         },
      },
      breakpoints: themeConfig.breakpoints
   }
   return createTheme(options)
}

export const muiAppBarStyleOverrides: CSSInterpolation = {
   borderRadius: '0px',
   '& .MuiButton-containedPrimary': {
      color: themeConfig.primary['800'],
      backgroundColor: themeConfig.cream['100'],
      '&:hover': {
         backgroundColor: themeConfig.light.action.selected,
      },
      // No disabled override: MUI's own contained-button default already reads
   }
}

export const hex2rgb = (hex: string, alpha?: number) => {
   const r = parseInt(hex.slice(1, 3), 16)
   const g = parseInt(hex.slice(3, 5), 16)
   const b = parseInt(hex.slice(5, 7), 16)

   if (Number.isNaN(alpha)) {
      return `rgb(${r}, ${g}, ${b})`
   } else if (!alpha) {
      throw new Error('Incorrect alpha value provided')
   }

   return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

interface PaletteExtensions {
   gap: {
      primary: {
         "50": string,
         "200": string,
         "400": string,
         "600": string,
         "800": string,
      },
      info: {
         "500": string
      },
   },
   passion: {
      main: string,
      light: string,
      dark: string,
      contrastText: string,
   }
}

declare module '@mui/material/SvgIcon' {
   interface SvgIconPropsSizeOverrides {
      'extra-small': true
      'extra-large': true
   }
}

declare module '@mui/material/Button' {
   interface ButtonPropsSizeOverrides {
      'extra-small': true
   }
}

declare module '@mui/material/styles' {
   interface BreakpointOverrides {
      xs: true
      sm: true
      md: true
      lg: true
      xl: true
      mobile: true
      tablet: true
      desktop: true
   }
   interface Palette extends PaletteExtensions { }
   interface PaletteOptions extends PaletteExtensions { }
   interface TypographyVariants {
      subtitle1Bold: React.CSSProperties
      subtitle2Bold: React.CSSProperties
      body1Bold: React.CSSProperties
      body2Bold: React.CSSProperties
   }

   interface TypographyVariantsOptions {
      subtitle1Bold?: React.CSSProperties
      subtitle2Bold?: React.CSSProperties
      body1Bold?: React.CSSProperties
      body2Bold?: React.CSSProperties
   }
}

declare module '@mui/material/Typography' {
   interface TypographyPropsVariantOverrides {
      subtitle1Bold: true
      subtitle2Bold: true
      body1Bold: true
      body2Bold: true
   }

   interface TypographyOwnProps {
      "max-lines"?: number
   }
}
// Extend Fab's color options to include gap.passion
declare module '@mui/material/Fab' {
   interface FabPropsColorOverrides {
      passion: true;
   }
}
